import React, { useState, useEffect, useRef, useCallback } from 'react';
import { debugError } from '../../utils/debug';
import {
  calculateCatalogAccessoryPrice,
  type AccessoryCatalogItem,
} from '../../services/summaryPriceCalculations';
import { priceService } from '../../services/priceService';
import { ConfiguratorState, Dimensions } from '../../types';
import { formatLinePrice } from '../../utils/priceFormat';
import { replaceUkTerm } from '../../utils/summaryHelpers';
import {
  calculateRequiredMotors,
  DEFAULT_MOTOR_RULES,
  MotorRules,
} from '../../config/priceConfigComfort';
import { ACCESS_MOTOR_RULES } from '../../config/priceConfigAccess';
import { ANTIRUTSCH_ACCESSORY_CODES } from '../../config/priceConfigWalkOn';

interface BackendAccessory {
  id: number;
  accessory_code: string;
  name: string;
  description: string;
  price_type: 'fixed' | 'per_sqm' | 'percentage' | 'conditional' | 'per_unit';
  base_price: number;
  glass_type_restriction: 'double' | 'triple' | 'both';
  category_name: string;
  category_id: number;
  is_required: boolean | number | string;
  is_default: boolean;
  active: boolean;
  min_quantity?: number;
  max_quantity?: number;
}

interface AccessoriesProps {
  state: ConfiguratorState;
  updateState: (updates: Partial<ConfiguratorState>) => void;
  onNext: () => void;
  onBack?: () => void;
  showGross?: boolean;
  catalog?: AccessoryCatalogItem[];
  onCatalogChange?: (items: AccessoryCatalogItem[]) => void;
}

const RAL_ACCESSORY_CODES = new Set([
  'ral',
  'ral_outside_standard',
  'ral_outside_custom',
  'ral_inside_standard',
  'ral_inside_custom',
]);

function asBackendAccessories(items: AccessoryCatalogItem[]): BackendAccessory[] {
  return items.map((item) => ({
    id: Number(item.id) || 0,
    accessory_code: String(item.accessory_code || item.id || ''),
    name: item.name || String(item.accessory_code || item.id || ''),
    description: (item as BackendAccessory).description || '',
    price_type: (item.price_type as BackendAccessory['price_type']) || 'fixed',
    base_price: Number(item.base_price) || 0,
    glass_type_restriction:
      (item.glass_type_restriction as BackendAccessory['glass_type_restriction']) ||
      'both',
    category_name: item.category_name || item.category || 'Sonstiges',
    category_id: (item as BackendAccessory).category_id || 0,
    is_required: item.is_required ?? false,
    is_default: Boolean(item.is_default),
    active: true,
    min_quantity: item.min_quantity,
    max_quantity: (item as BackendAccessory).max_quantity,
  }));
}

const isTruthyRequired = (v: BackendAccessory['is_required']) =>
  v === 1 || v === '1' || v === true;

/** Category sort order — matches the legacy priorities so the user's
 *  first-glance experience stays consistent across releases. */
const CATEGORY_ORDER = [
  'Glasoptionen',
  'Begehbarkeit',
  'Motoren & Steuerung',
  'Farbauswahl',
  'Services',
  'Sonstiges',
];

function AccessoryCard({
  accessory,
  isSelected,
  isDisabled,
  isRequired,
  isComfortControl,
  isAutoMotor,
  product,
  dimensions,
  onToggle,
  showGross,
}: {
  accessory: BackendAccessory;
  isSelected: boolean;
  isDisabled: boolean;
  isRequired: boolean;
  isComfortControl: boolean;
  isAutoMotor: boolean;
  product: string;
  dimensions: Dimensions;
  onToggle: () => void;
  showGross: boolean;
}) {
  const clickable = !isDisabled && !isRequired && !isAutoMotor;
  const net = calculateCatalogAccessoryPrice(accessory, product, dimensions);
  const priceLabel =
    net === 0
      ? 'Inklusive'
      : `+ ${formatLinePrice(net, showGross)}`;

  const meta = isAutoMotor
    ? 'automatisch'
    : isRequired
      ? 'erforderlich'
      : isDisabled
        ? `nur bei ${accessory.glass_type_restriction === 'triple' ? '3-fach' : '2-fach'}`
        : isComfortControl
          ? 'optional'
          : isSelected
            ? 'gewählt'
            : null;

  return (
    <div
      role={clickable ? 'checkbox' : undefined}
      aria-checked={clickable ? isSelected : undefined}
      aria-disabled={isDisabled || isRequired || isAutoMotor}
      aria-label={accessory.name}
      tabIndex={clickable ? 0 : -1}
      onClick={() => clickable && onToggle()}
      onKeyDown={(e) => {
        if (!clickable) return;
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onToggle();
        }
      }}
      className={`sv-option-card ${isSelected ? 'is-selected' : ''} ${
        isDisabled ? 'is-disabled' : ''
      } ${clickable ? '' : 'is-static'}`}
    >
      <div className="sv-option-card-top">
        <h3 className="sv-option-card-name">{accessory.name}</h3>
        <span className="sv-option-card-price">{priceLabel}</span>
      </div>
      {accessory.description && (
        <p className="sv-option-card-desc">{replaceUkTerm(accessory.description)}</p>
      )}
      {meta && <span className="sv-option-card-meta">{meta}</span>}
    </div>
  );
}

export default function Accessories({
  state,
  updateState,
  onNext,
  onBack,
  showGross = true,
  catalog = [],
  onCatalogChange,
}: AccessoriesProps) {
  const [accessories, setAccessories] = useState<BackendAccessory[]>(() =>
    asBackendAccessories(catalog),
  );
  const [isLoading, setIsLoading] = useState(catalog.length === 0);
  const [customRal, setCustomRal] = useState<string>('');
  const [customRalInside, setCustomRalInside] = useState<string>('');
  const [showRalInput, setShowRalInput] = useState(false);
  const [showRalInsideInput, setShowRalInsideInput] = useState(false);

  // Non-blocking notice banner. Timer clears itself after 5 s.
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimerRef = useRef<number | null>(null);
  const showNotice = useCallback((message: string) => {
    setNotice(message);
    if (typeof window === 'undefined') return;
    if (noticeTimerRef.current !== null)
      window.clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = window.setTimeout(() => {
      setNotice(null);
      noticeTimerRef.current = null;
    }, 5000);
  }, []);
  useEffect(
    () => () => {
      if (noticeTimerRef.current !== null && typeof window !== 'undefined')
        window.clearTimeout(noticeTimerRef.current);
    },
    [],
  );

  const isCircular = state.product === 'SkyVision CIRCULAR';
  const isComfort = state.product === 'SkyVision COMFORT';
  const isAccess = state.product === 'SkyVision ACCESS';
  const isMotorProduct = isComfort || isAccess;
  const pricedUpdate = (patch: Partial<ConfiguratorState>) => {
    updateState(patch);
  };

  useEffect(() => {
    if (catalog.length > 0) {
      setAccessories(asBackendAccessories(catalog));
      setIsLoading(false);
    }
  }, [catalog]);

  useEffect(() => {
    if (!state.product) {
      setAccessories([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setAccessories(catalog.length > 0 ? asBackendAccessories(catalog) : []);
    if (catalog.length === 0) setIsLoading(true);
    priceService
      .getAccessories(state.product, { refresh: true })
      .then((items) => {
        if (cancelled) return;
        setAccessories(asBackendAccessories(items));
        onCatalogChange?.(items);
      })
      .catch((err) => {
        if (cancelled) return;
        debugError('Fehler beim Laden der Accessories:', err);
        if (catalog.length === 0) setAccessories([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // catalog/onCatalogChange are seeds and a stable setter; product drives the refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.product]);

  const handleToggle = (code: string) => {
    const accessory = accessories.find((a) => a.accessory_code === code);
    if (!accessory) return;

    if (
      accessory.glass_type_restriction !== 'both' &&
      accessory.glass_type_restriction !== state.glassType
    ) {
      showNotice(
        `„${accessory.name}" ist nur bei ${
          accessory.glass_type_restriction === 'triple' ? '3-fach' : '2-fach'
        } Verglasung verfügbar.`,
      );
      return;
    }

    // COMFORT auto-motors: user can't toggle them.
    if (isMotorProduct && (code === 'motor_single' || code === 'motor_dual')) {
      showNotice(
        isAccess
          ? 'ACCESS hat fest zwei Klapparm-Antriebe. Die Anzahl ist nicht wählbar.'
          : 'Motoren werden bei COMFORT automatisch anhand Maßen und Verglasung gewählt.',
      );
      return;
    }

    // COMFORT/ACCESS control types are exclusive (wcc103 XOR wcc106).
    if (isMotorProduct && (code === 'wcc103' || code === 'wcc106')) {
      const others = state.accessories.filter(
        (id) => id !== 'wcc103' && id !== 'wcc106',
      );
      const alreadyOn = state.accessories.includes(code);
      pricedUpdate({
        accessories: alreadyOn ? others : [...others, code],
        controlType: alreadyOn ? null : (code as 'wcc103' | 'wcc106'),
      });
      return;
    }

    // Antirutsch is exclusive (print XOR structure).
    if ((ANTIRUTSCH_ACCESSORY_CODES as readonly string[]).includes(code)) {
      const others = state.accessories.filter(
        (id) => !(ANTIRUTSCH_ACCESSORY_CODES as readonly string[]).includes(id),
      );
      const alreadyOn = state.accessories.includes(code);
      pricedUpdate({
        accessories: alreadyOn ? others : [...others, code],
      });
      return;
    }

    // Required accessories can't be turned off once on.
    if (isTruthyRequired(accessory.is_required) && state.accessories.includes(code)) {
      showNotice(
        `„${accessory.name}" ist erforderlich und kann nicht abgewählt werden.`,
      );
      return;
    }

    const next = state.accessories.includes(code)
      ? state.accessories.filter((id) => id !== code)
      : [...state.accessories, code];
    pricedUpdate({ accessories: next });
  };

  const handleRalToggle = (useCustom: boolean) => {
    setShowRalInput(useCustom);
    if (!useCustom) {
      setCustomRal('');
      pricedUpdate({ ralColor: 'RAL 7043' });
      return;
    }
    pricedUpdate({
      ralColor: customRal.trim() ? `RAL ${customRal.trim()}` : 'RAL Sonderfarbe',
    });
  };
  const handleRalInsideToggle = (useCustom: boolean) => {
    setShowRalInsideInput(useCustom);
    if (!useCustom) {
      setCustomRalInside('');
      pricedUpdate({ ralColorInside: 'RAL 9010' });
      return;
    }
    pricedUpdate({
      ralColorInside: customRalInside.trim()
        ? `RAL ${customRalInside.trim()}`
        : 'RAL Sonderfarbe',
    });
  };
  const commitCustomRal = () => {
    if (customRal.trim()) pricedUpdate({ ralColor: `RAL ${customRal.trim()}` });
  };
  const commitCustomRalInside = () => {
    if (customRalInside.trim())
      pricedUpdate({ ralColorInside: `RAL ${customRalInside.trim()}` });
  };

  const requiredMotors = isAccess
    ? 2
    : isComfort
    ? calculateRequiredMotors(
        state.dimensions.width,
        state.dimensions.length,
        (state.glassType as 'double' | 'triple') || 'double',
        state.motorRules || DEFAULT_MOTOR_RULES,
      )
    : 1;
  const requiredMotorCode =
    requiredMotors === 2 ? 'motor_dual' : 'motor_single';

  // Group accessories by category, then sort by CATEGORY_ORDER.
  const groupedAccessories = accessories.reduce(
    (acc, item) => {
      const cat = item.category_name || 'Sonstiges';
      (acc[cat] = acc[cat] || []).push(item);
      return acc;
    },
    {} as Record<string, BackendAccessory[]>,
  );
  const orderedCategories = Object.keys(groupedAccessories).sort((a, b) => {
    const ia = CATEGORY_ORDER.indexOf(a);
    const ib = CATEGORY_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  /** Filters accessories inside a category before rendering. Same rules
   *  as the legacy component. */
  const filterForRender = (items: BackendAccessory[]) =>
    items.filter((a) => {
      if (!a.accessory_code) return false;
      if (RAL_ACCESSORY_CODES.has(a.accessory_code)) return false;
      if (
        a.glass_type_restriction &&
        a.glass_type_restriction !== 'both' &&
        a.glass_type_restriction !== state.glassType
      ) {
        return false;
      }
      if ((ANTIRUTSCH_ACCESSORY_CODES as readonly string[]).includes(a.accessory_code)) {
        if (state.product !== 'SkyVision WALKON' && state.product !== 'SkyVision LINEAR WALK-ON' && state.product !== 'SkyVision CIRCULAR WALK-ON') {
          return false;
        }
      }
      if (
        isMotorProduct &&
        (a.accessory_code === 'motor_single' || a.accessory_code === 'motor_dual')
      ) {
        return a.accessory_code === requiredMotorCode;
      }
      if (isMotorProduct && (a.accessory_code === 'wcc103' || a.accessory_code === 'wcc106')) {
        if (a.accessory_code === 'wcc103' && requiredMotors !== 1) return false;
        if (a.accessory_code === 'wcc106' && requiredMotors !== 2) return false;
      }
      return true;
    });

  const extraCategories = orderedCategories.filter((c) => c !== 'Farbauswahl');
  const colorItems = groupedAccessories['Farbauswahl'] || [];

  const renderExtraSection = (categoryName: string) => {
    const items = groupedAccessories[categoryName] || [];
    const filtered = filterForRender(items);
    if (filtered.length === 0 && categoryName !== 'Motoren & Steuerung') return null;

    return (
      <section key={categoryName} className="sv-acc-section">
        <h3 className="sv-acc-title">{categoryName}</h3>

        {categoryName === 'Motoren & Steuerung' && isMotorProduct && (
          <MotorInfoBlock
            requiredMotors={requiredMotors}
            width={state.dimensions.width}
            length={state.dimensions.length}
            glassType={state.glassType}
            rules={isAccess ? ACCESS_MOTOR_RULES : state.motorRules || DEFAULT_MOTOR_RULES}
            accessFixed={isAccess}
          />
        )}

        {filtered.length > 0 && (
          <div className={`sv-acc-grid ${filtered.length > 1 ? 'is-2' : ''}`}>
            {filtered.map((a) => (
              <AccessoryCard
                key={a.accessory_code}
                accessory={a}
                product={state.product}
                dimensions={state.dimensions}
                isSelected={
                  isMotorProduct &&
                  (a.accessory_code === 'motor_single' ||
                    a.accessory_code === 'motor_dual')
                    ? a.accessory_code === requiredMotorCode
                    : state.accessories.includes(a.accessory_code)
                }
                isDisabled={
                  a.glass_type_restriction !== 'both' &&
                  a.glass_type_restriction !== state.glassType
                }
                isRequired={
                  isMotorProduct &&
                  (a.accessory_code === 'wcc103' || a.accessory_code === 'wcc106')
                    ? false
                    : isTruthyRequired(a.is_required)
                }
                isComfortControl={
                  isMotorProduct &&
                  (a.accessory_code === 'wcc103' || a.accessory_code === 'wcc106')
                }
                isAutoMotor={
                  isMotorProduct &&
                  (a.accessory_code === 'motor_single' ||
                    a.accessory_code === 'motor_dual')
                }
                onToggle={() => handleToggle(a.accessory_code)}
                showGross={showGross}
              />
            ))}
          </div>
        )}
      </section>
    );
  };

  const renderColorSection = () => {
    if (colorItems.length === 0) return null;
    const outsideItems = colorItems.filter((a) => a.accessory_code.includes('outside'));
    const insideItems = colorItems.filter((a) => a.accessory_code.includes('inside'));
    const otherColors = colorItems.filter(
      (a) => !a.accessory_code.includes('outside') && !a.accessory_code.includes('inside'),
    );

    return (
      <section className="sv-acc-section">
        <h3 className="sv-acc-title">Farbauswahl</h3>
        <div className="sv-acc-layout">
          {outsideItems.length > 0 && (
            <RalPicker
              label="Außen"
              note="Standard RAL 7043. Glas mit 115 mm Siebdruckkante als UV-Schutz."
              items={outsideItems}
              activeCustom={showRalInput}
              onToggle={handleRalToggle}
              customValue={customRal}
              setCustomValue={(v) => {
                setCustomRal(v);
                if (v.trim()) pricedUpdate({ ralColor: `RAL ${v.trim()}` });
              }}
              commit={commitCustomRal}
              standardCode="ral_outside_standard"
              swatchColor="#2E3236"
              showGross={showGross}
            />
          )}
          {!isCircular && insideItems.length > 0 && (
            <RalPicker
              label="Innen"
              note="Standard RAL 9010 Reinweiß."
              items={insideItems}
              activeCustom={showRalInsideInput}
              onToggle={handleRalInsideToggle}
              customValue={customRalInside}
              setCustomValue={(v) => {
                setCustomRalInside(v);
                if (v.trim())
                  pricedUpdate({ ralColorInside: `RAL ${v.trim()}` });
              }}
              commit={commitCustomRalInside}
              standardCode="ral_inside_standard"
              swatchColor="#FFFFFF"
              showGross={showGross}
            />
          )}
        </div>
        {otherColors.length > 0 && (
          <div className={`sv-acc-grid ${otherColors.length > 1 ? 'is-2' : ''} mt-6`}>
            {otherColors.map((a) => (
              <AccessoryCard
                key={a.accessory_code}
                accessory={a}
                product={state.product}
                dimensions={state.dimensions}
                isSelected={state.accessories.includes(a.accessory_code)}
                isDisabled={
                  a.glass_type_restriction !== 'both' &&
                  a.glass_type_restriction !== state.glassType
                }
                isRequired={isTruthyRequired(a.is_required)}
                isComfortControl={false}
                isAutoMotor={false}
                onToggle={() => handleToggle(a.accessory_code)}
                showGross={showGross}
              />
            ))}
          </div>
        )}
      </section>
    );
  };

  return (
    <div>
      <h2 className="sr-only">Zubehör auswählen</h2>

      <p className="sv-lede mb-8 max-w-2xl">
        Glas, Farbe und Services. Jede Wahl erscheint sofort im Preis unten.
      </p>

      {notice && (
        <div className="sv-acc-notice mb-6" role="status" aria-live="polite">
          <p className="text-[13px] text-ink-2 leading-snug flex-1">{notice}</p>
          <button
            type="button"
            aria-label="Hinweis schließen"
            onClick={() => setNotice(null)}
            className="sv-option-card-price text-ink-3"
          >
            ×
          </button>
        </div>
      )}

      {isLoading ? (
        <div
          className="sv-hairline p-8 min-h-[200px] flex items-center text-[11px] text-ink-3"
          aria-busy="true"
        >
          Zubehör wird geladen …
        </div>
      ) : accessories.length === 0 ? (
        <div className="sv-hairline p-6">
          <p className="text-[13px] text-ink-2 leading-snug">
            Für dieses Produkt ist aktuell kein Zubehör hinterlegt. Sie können
            den Konfigurator trotzdem abschließen.
          </p>
        </div>
      ) : (
        <div className="sv-acc-stack">
          <div className="sv-acc-col">{extraCategories.map(renderExtraSection)}</div>
          {renderColorSection()}
        </div>
      )}
    </div>
  );
}

/** RAL picker — two option cards (standard vs. custom). */
function RalPicker({
  label,
  note,
  items,
  activeCustom,
  onToggle,
  customValue,
  setCustomValue,
  commit,
  standardCode,
  swatchColor,
  showGross,
}: {
  label: string;
  note: string;
  items: BackendAccessory[];
  activeCustom: boolean;
  onToggle: (useCustom: boolean) => void;
  customValue: string;
  setCustomValue: (v: string) => void;
  commit: () => void;
  standardCode: string;
  swatchColor: string;
  showGross: boolean;
}) {
  const standard = items.find((i) => i.accessory_code === standardCode);
  const nonStandard = items.filter((i) => i.accessory_code !== standardCode);

  return (
    <div>
      <p className="sv-acc-subtitle">{label}</p>
      <p className="sv-option-card-desc mb-3">{note}</p>
      <div className="sv-acc-grid" role="radiogroup" aria-label={label}>
        {standard && (
          <div
            role="radio"
            aria-checked={!activeCustom}
            aria-label={standard.name}
            tabIndex={0}
            onClick={() => onToggle(false)}
            onKeyDown={(e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                onToggle(false);
              }
            }}
            className={`sv-option-card ${!activeCustom ? 'is-selected' : ''}`}
          >
            <div className="sv-option-card-top">
              <h4 className="sv-option-card-name">
                <span className="sv-option-swatch" style={{ backgroundColor: swatchColor }} aria-hidden />
                {standard.name}
              </h4>
              <span className="sv-option-card-price">Inklusive</span>
            </div>
            <p className="sv-option-card-desc">{replaceUkTerm(standard.description)}</p>
          </div>
        )}
        {nonStandard.map((item) => (
          <div
            key={item.accessory_code}
            role="radio"
            aria-checked={activeCustom}
            aria-label={item.name}
            tabIndex={0}
            onClick={() => onToggle(true)}
            onKeyDown={(e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                onToggle(true);
              }
            }}
            className={`sv-option-card ${activeCustom ? 'is-selected' : ''}`}
          >
            <div className="sv-option-card-top">
              <h4 className="sv-option-card-name">{item.name}</h4>
              <span className="sv-option-card-price">
                {item.base_price === 0
                  ? 'Inklusive'
                  : `+ ${formatLinePrice(item.base_price, showGross)}`}
              </span>
            </div>
            <p className="sv-option-card-desc">{replaceUkTerm(item.description)}</p>
            {activeCustom && (
              <div
                className="sv-ral-input"
                onClick={(e) => e.stopPropagation()}
              >
                <span>RAL</span>
                <input
                  type="text"
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  onBlur={commit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commit();
                    }
                  }}
                  placeholder="9005"
                  aria-label="RAL-Nummer"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MotorInfoBlock({
  requiredMotors,
  width,
  length,
  glassType,
  rules,
  accessFixed = false,
}: {
  requiredMotors: number;
  width: number;
  length: number;
  glassType: string | null;
  rules: MotorRules;
  accessFixed?: boolean;
}) {
  if (accessFixed) {
    return (
      <div className="sv-acc-note mb-4">
        <p className="text-[13px] text-ink leading-snug">
          ACCESS öffnet mit zwei verdeckt liegenden Klapparm-Antrieben bis 75°.
          Die Anzahl ist fest — nicht abhängig von Maß oder Verglasung.
        </p>
      </div>
    );
  }
  const area = (width * length) / 1_000_000;
  const maxSide = Math.max(width, length);
  const areaLimit =
    glassType === 'triple' ? rules.dualAreaTriple : rules.dualAreaDouble;
  const reason = (() => {
    if (area > areaLimit)
      return `Das Bestellmaß von ${area.toFixed(2)} m² überschreitet bei ${
        glassType === 'triple' ? '3-fach' : '2-fach'
      } das Maximum von ${areaLimit} m² für einen Motor.`;
    if (maxSide > rules.dualMaxSide)
      return `Eine Seite (${maxSide} mm) überschreitet ${rules.dualMaxSide} mm — dafür ist ein zweiter Motor nötig.`;
    return null;
  })();

  return (
    <div className="sv-acc-note mb-4">
      <p className="text-[13px] text-ink leading-snug">
        Bei {width} × {length} mm und {glassType === 'triple' ? '3-fach' : '2-fach'}{' '}
        Verglasung werden{' '}
        <span className="text-rubric">
          {requiredMotors} Motor{requiredMotors > 1 ? 'en' : ''}
        </span>{' '}
        automatisch eingesetzt.
      </p>
      {requiredMotors === 2 && reason && (
        <p className="mt-2 text-[13px] text-ink-2 leading-snug">{reason}</p>
      )}
    </div>
  );
}
